/**
 * Creates a query object from contexts, filters, and traversals
 * @param {Array<Object>} contexts - Array of context objects (must not be empty)
 * @param {Array<Object>} filters - Array of filter objects
 * @param {number} traversals - Number of traversal levels (1-3)
 * @returns {Object} Query object
 * @throws {Error} If validation fails
 */
export const createQuery = (contexts, filters, traversals) => {
  // Validate inputs
  if (!Array.isArray(contexts) || contexts.length === 0) {
    throw new Error('At least one context is required');
  }

  if (typeof traversals !== 'number' || traversals < 1 || traversals > 3) {
    throw new Error('Traversals must be a number between 1 and 3');
  }

  const query = {
    context: contexts,
    filters: filters && filters.length > 0 ? filters : [
      {
        type: 'edge',
        label: '',
        direction: 'both',
        filterValue: null,
        condition: 'none',
        filters: [],
      },
    ],
    traversals,
  };
  return query;
};

export const getNextMinTraversal = (contextsMap, contextToIgnore) => {
  let min = 3;
  for (const contextId in contextsMap) {
    if (contextsMap[contextId] < min) {
      min = contextsMap[contextId];
    }
  }

  return min;
};

export const readBlob = async (blob, isNlQuery = false) => {
  try {
    if (!blob) {
      return null;
    }

    const response = await fetch(blob);
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      console.error('Error reading blob: non-OK response', {
        status: response.status,
        statusText: response.statusText,
        preview: text?.slice?.(0, 200),
      });
      return null;
    }

    const jsonResponse = await response.json();
    if (isNlQuery) {
      const graph = jsonResponse?.graph ?? jsonResponse?.Graph;
      return {
        edges: graph?.Edges ?? graph?.edges ?? null,
        vertices: graph?.Vertices ?? graph?.vertices ?? null,
        responseGenerated:
          graph?.ResponseGenerated ?? graph?.responseGenerated ?? null,
      };
    }
    const totalMatchCount =
      jsonResponse?.TotalMatchCount ?? jsonResponse?.totalMatchCount;
    // Normalize keys to lowercase for consistency across the app
    const result = {
      edges: jsonResponse?.Edges ?? jsonResponse?.edges ?? null,
      vertices: jsonResponse?.Vertices ?? jsonResponse?.vertices ?? null,
      responseGenerated:
        jsonResponse?.ResponseGenerated ?? jsonResponse?.responseGenerated ?? null,
    };
    if (totalMatchCount !== undefined && totalMatchCount !== null) {
      result.totalMatchCount = totalMatchCount;
    }
    return result;
  } catch (error) {
    console.error('Error reading blob', error);
    return null;
  }
};
